/*
 * Copyright 2015 Google Inc. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// Disable distortion provided by the boilerplate because we are doing
// vertex-based distortion.
window.THREE = require('three');
var WebVRConfig = window.WebVRConfig || {}
WebVRConfig.PREVENT_DISTORTION = true;

// Initialize the loading indicator as quickly as possible to give the user
// immediate feedback.
var LoadingIndicator = require('./loading-indicator');
var loadIndicator = new LoadingIndicator();

// Include relevant polyfills.
require('../node_modules/webvr-polyfill/build/webvr-polyfill');
var ES6Promise = require('es6-promise');
// Polyfill ES6 promises for IE.
ES6Promise.polyfill();

var PhotosphereRenderer = require('./photosphere-renderer');
var SceneLoader = require('./scene-loader');
var Stats = require('../node_modules/stats-js/build/stats.min');
var Util = require('./util');

var _root;
var stats = new Stats();
var renderer;

var loader = new SceneLoader();
loader.on('error', onSceneError);
loader.on('load', onSceneLoad);

var videoElement = null;
// TODO: Make this not global.
// Currently global in order to allow callbacks.
var loadedScene = null;
var anim;

/*
var requestAnimationFrame = window.requestAnimationFrame || window.mozRequestAnimationFrame ||
                            window.webkitRequestAnimationFrame || window.msRequestAnimationFrame;

var cancelAnimationFrame = window.cancelAnimationFrame || window.mozCancelAnimationFrame;
*/

var lastTime = 0;
var vendors = ['ms', 'moz', 'webkit', 'o'];
for(var x = 0; x < vendors.length && !window.requestAnimationFrame; ++x) {
    window.requestAnimationFrame = window[vendors[x]+'RequestAnimationFrame'];
    window.cancelAnimationFrame = window[vendors[x]+'CancelAnimationFrame'] 
                               || window[vendors[x]+'CancelRequestAnimationFrame'];
}

if (!window.requestAnimationFrame)
    window.requestAnimationFrame = function(callback, element) {
        var currTime = new Date().getTime();
        var timeToCall = Math.max(0, 16 - (currTime - lastTime));
        var id = window.setTimeout(function() { callback(currTime + timeToCall); }, 
          timeToCall);
        lastTime = currTime + timeToCall;
        return id;
    };

if (!window.cancelAnimationFrame)
    window.cancelAnimationFrame = function(id) {
        clearTimeout(id);
    };

function init(docEl, data) {
  loadIndicator.show();

  if (!Util.isWebGLEnabled()) {
    showError('WebGL not supported.');
    return;
  }

  /*if(typeof renderer !== 'undefined'){
    renderer.destroy();
    renderer = null;
  }*/
  
  renderer = new PhotosphereRenderer(docEl);
  renderer.on('error', onRenderError);
  
  // Load the scene.
  loader.loadScene(data);

  if (Util.getQueryParameter('debug')) {
    showStats();
  }

  _root = docEl;
}

function mute() {

  if(videoElement) {
    videoElement.muted = true;
  }
}

function unmute() {

  if(videoElement) {
    videoElement.muted = false;
  }
}

function loadImage(src, params) {
  renderer.on('load', onRenderLoad);
  renderer.setPhotosphere(src, params);
}

function onSceneLoad(scene) {
  if (!scene || !scene.isComplete()) {
    showError('Scene failed to load');
    return;
  }

  loadedScene = scene;

  var params = {
    isStereo: scene.isStereo,
  }

  renderer.setDefaultLookDirection(scene.yaw || 0);

  if (scene.preview) {
    var onPreviewLoad = function() {
      loadIndicator.hide();
      renderer.removeListener('load', onPreviewLoad);
      renderer.setPhotosphere(scene.image, params);
    }
    renderer.removeListener('load', onRenderLoad);
    renderer.on('load', onPreviewLoad);
    renderer.setPhotosphere(scene.preview, params);
  } else if (scene.video) {
    // if(false) {
    // //if (Util.isIOS() || Util.isIE11()) {
    //   // On iOS and IE 11, if an 'image' param is provided, load it instead of
    //   // showing an error.
    //   //
    //   // TODO(smus): Once video textures are supported, remove this fallback.
    //   if (scene.image) {
    //     loadImage(scene.image, params);
    //   } else {
    //     showError('Video is not supported on this platform (iOS or IE11).');
    //     return;
    //   }
    // }

    // Load the video element.
    if(!videoElement) {
      videoElement = document.createElement('video');
    }
    videoElement.setAttribute('muted', true);
    videoElement.setAttribute('loop', true);
    videoElement.setAttribute('playsinline', true);
    videoElement.setAttribute('crossorigin', 'anonymous');
    videoElement.addEventListener('canplaythrough', onVideoLoad);
    videoElement.addEventListener('load', onVideoLoad);
    videoElement.addEventListener('error', onVideoError);
    videoElement.src = scene.video;
    videoElement.muted = true;
    // videoElement.play();
    videoElement.load();
    videoElement.play();

    // document.appendChild(videoElement);
  } else if (scene.image) {
    // Otherwise, just render the photosphere.
    loadImage(scene.image, params);
  }

  if(typeof cancelAnimationFrame != 'undefined' && typeof anim != 'undefined') {
    cancelAnimationFrame(anim);
  }
  anim = requestAnimationFrame(loop);
}

function onVideoLoad() {
  // Render the stereo video.
  var params = {
    isStereo: loadedScene.isStereo,
  }

  renderer.set360Video(videoElement, params);

  videoElement.play();
  // On mobile, tell the user they need to tap to start. Otherwise, autoplay.
  // if (!Util.isMobile()) {
  //   // Hide loading indicator.
  //   // Autoplay the video on desktop.
  //   videoElement.play();
  // } else {
  //   // Tell user to tap to start.
  //   // showError('Tap to start video', 'Play');
  //   _root.addEventListener('touchend', onVideoTap);
  // }

  videoElement.removeEventListener('canplaythrough', onVideoLoad);
  videoElement.removeEventListener('load', onVideoLoad);

  loadIndicator.hide();
}

function onVideoTap() {
  hideError();
  videoElement.play();

  // Prevent multiple play() calls on the video element.
  _root.removeEventListener('touchend', onVideoTap);
}

function onRenderLoad() {
  // Hide loading indicator.
  loadIndicator.hide();
}

function onSceneError(message) {
  showError('Loader: ' + message);
}

function onRenderError(message) {
  showError('Render: ' + message);
}

function onVideoError(e) {
  showError('Video load error');
  console.log(e);
}

function showError(message, opt_title) {
  // Hide loading indicator.
  loadIndicator.hide();

  console.log('error:', message);
}

function hideError() {
  //var error = document.querySelector('#error');
  
  //error.classList.remove('visible');
}

function showStats() {
  stats.setMode(0); // 0: fps, 1: ms

  // Align bottom-left.
  stats.domElement.style.position = 'absolute';
  stats.domElement.style.left = '0px';
  stats.domElement.style.bottom = '0px';
  _root.appendChild(stats.domElement);
}

function loop(time) {
  renderer.render(time);
  anim = requestAnimationFrame(loop);
}

module.exports = {
  init : init,
  mute : mute,
  unmute : unmute
}
